import { Injectable } from "@angular/core";
import { BehaviorSubject, Subject } from "rxjs";
import { NoteContext } from "../domains/note";

export interface NoteRouteContextPattern {
  objectType: string;
  matcher: RegExp;
  fallbackLabel: string;
}

/**
 * Service de contexte pour les notes.
 *
 * Fait le lien entre les modules metier (client, chantier, etc.)
 * et le drawer Notes. Les modules appellent setContext() + openDrawer(),
 * le composant eva-notes-todos reagit en ouvrant le drawer avec le filtre.
 */
@Injectable({
  providedIn: "root",
})
export class NoteContextService {
  // ==========================================================================
  // State
  // ==========================================================================

  private contextSubject = new BehaviorSubject<NoteContext | null>(null);
  public context$ = this.contextSubject.asObservable();

  /** Evenement pour ouvrir le drawer Notes avec un contexte optionnel */
  public drawerOpen$ = new Subject<NoteContext | null>();

  /** Evenement pour ouvrir le drawer Tâches avec un contexte objet */
  /** Ouverture du galet sur Conversations (placeholder tant que la messagerie n'existe pas) */
  public drawerConversationsOpen$ = new Subject<void>();
  /** Ouverture du galet sur l'Assistant (question posée sur l'accueil, « Réduire » depuis /ai) */
  public drawerAssistantOpen$ = new Subject<void>();
  public drawerTasksOpen$ = new Subject<{ object_id: string; object_type: string; object_name?: string } | null>();

  /** Cache de labels pour reconstruire un contexte depuis une URL */
  private labelsByObject = new Map<string, string>();

  /**
   * Mapping route -> contexte notes (pages de détail, avec objectId dans l'URL).
   * Extensible via registerRouteContextPattern() pour les futurs modules.
   */
  private routeContextPatterns: NoteRouteContextPattern[] = [
    {
      objectType: "CUSTOMER",
      matcher: /^\/customers\/([^\/?#]+)(?:\/|$)/i,
      fallbackLabel: "Client",
    },
    {
      objectType: "PROJECT",
      matcher: /^\/projects\/([^\/?#]+)(?:\/|$)/i,
      fallbackLabel: "Chantier",
    },
    {
      objectType: "OFFER",
      matcher: /^\/offers(?:-v1)?\/([^\/?#]+)(?:\/|$)/i,
      fallbackLabel: "Offre",
    },
    {
      objectType: "INVOICE",
      matcher: /^\/invoices\/issued\/([^\/?#]+)(?:\/|$)/i,
      fallbackLabel: "Facture",
    },
    {
      objectType: "INTERVENTION",
      matcher: /^\/interventions\/([^\/?#]+)(?:\/|$)/i,
      fallbackLabel: "Intervention",
    },
    {
      objectType: "SUPPLIER",
      matcher: /^\/suppliers\/([^\/?#]+)(?:\/|$)/i,
      fallbackLabel: "Fournisseur",
    },
    {
      objectType: "EMPLOYEE",
      matcher: /^\/employees\/([^\/?#]+)(?:\/|$)/i,
      fallbackLabel: "Collaborateur",
    },
    {
      objectType: "PURCHASE_ORDER",
      matcher: /^\/purchase-orders\/([^\/?#]+)(?:\/|$)/i,
      fallbackLabel: "Bon de commande",
    },
  ];

  /**
   * Patterns pour les pages de liste (sans objectId).
   * Retournent un contexte avec objectId vide pour agréger toutes les notes du type.
   */
  private listContextPatterns: { objectType: string; matcher: RegExp; fallbackLabel: string }[] = [
    { objectType: "CUSTOMER", matcher: /^\/customers(?:\/)?$/i, fallbackLabel: "Clients & Prospects" },
    { objectType: "PROJECT", matcher: /^\/projects(?:\/)?$/i, fallbackLabel: "Chantiers" },
    { objectType: "OFFER", matcher: /^\/offers(?:-v1)?(?:\/)?$/i, fallbackLabel: "Offres" },
    { objectType: "INVOICE", matcher: /^\/invoices\/issued(?:\/)?$/i, fallbackLabel: "Factures clients" },
    { objectType: "INTERVENTION", matcher: /^\/interventions(?:\/)?$/i, fallbackLabel: "Interventions" },
    { objectType: "SUPPLIER", matcher: /^\/suppliers(?:\/)?$/i, fallbackLabel: "Fournisseurs" },
    { objectType: "EMPLOYEE", matcher: /^\/employees(?:\/)?$/i, fallbackLabel: "Collaborateurs" },
    { objectType: "PURCHASE_ORDER", matcher: /^\/purchase-orders(?:\/)?$/i, fallbackLabel: "Bons de commande" },
  ];

  // ==========================================================================
  // Methods
  // ==========================================================================

  /**
   * Definit le contexte de rattachement courant.
   */
  setContext(objectType: string, objectId: string, objectLabel: string): void {
    this.rememberObjectLabel(objectType, objectId, objectLabel);
    this.contextSubject.next({ objectType, objectId, objectLabel });
  }

  /**
   * Définit le contexte courant à partir d'un objet NoteContext.
   */
  setContextObject(context: NoteContext | null): void {
    if (!context) {
      this.clearContext();
      return;
    }
    this.setContext(context.objectType, context.objectId, context.objectLabel);
  }

  /**
   * Efface le contexte.
   */
  clearContext(): void {
    this.contextSubject.next(null);
  }

  /**
   * Ouvre le drawer avec le contexte courant.
   */
  openDrawer(context: NoteContext | null = this.contextSubject.getValue()): void {
    this.drawerOpen$.next(context);
  }

  /**
   * Ouvre le drawer directement pour un objet métier.
   */
  openDrawerForObject(objectType: string, objectId: string, objectLabel: string): void {
    this.setContext(objectType, objectId, objectLabel);
    this.openDrawer(this.getCurrentContext());
  }

  openDrawerConversations(): void {
    this.drawerConversationsOpen$.next();
  }

  openDrawerAssistant(): void {
    this.drawerAssistantOpen$.next();
  }

  /**
   * Ouvre le drawer Tâches pour un objet métier.
   */
  openDrawerTasksForObject(objectType: string, objectId: string, objectName?: string): void {
    this.drawerTasksOpen$.next({ object_id: objectId, object_type: objectType, object_name: objectName });
  }

  /**
   * Enregistre un pattern de résolution de contexte par URL.
   * Permet une extension simple pour les futurs modules.
   */
  registerRouteContextPattern(pattern: NoteRouteContextPattern): void {
    const exists = this.routeContextPatterns.some(
      (p) => p.objectType === pattern.objectType && p.matcher.source === pattern.matcher.source,
    );
    if (!exists) {
      this.routeContextPatterns.push(pattern);
    }
  }

  /**
   * Résout un contexte notes depuis l'URL courante.
   * - Pages de détail (avec ID) → contexte avec objectId
   * - Pages de liste (sans ID) → contexte avec objectId vide pour agréger par type
   */
  resolveContextFromUrl(url: string): NoteContext | null {
    const normalizedUrl = this.normalizeUrl(url);

    // 1. Pages de détail : objectId extrait de l'URL
    for (const pattern of this.routeContextPatterns) {
      const match = normalizedUrl.match(pattern.matcher);
      if (!match || !match[1]) continue;

      const objectId = match[1];
      const cacheKey = this.buildObjectKey(pattern.objectType, objectId);
      const cachedLabel = this.labelsByObject.get(cacheKey);

      return {
        objectType: pattern.objectType,
        objectId,
        objectLabel: cachedLabel || pattern.fallbackLabel,
      };
    }

    // 2. Pages de liste : pas d'objectId, agrégation par type
    for (const pattern of this.listContextPatterns) {
      if (pattern.matcher.test(normalizedUrl)) {
        return {
          objectType: pattern.objectType,
          objectId: "",
          objectLabel: pattern.fallbackLabel,
        };
      }
    }

    return null;
  }

  /**
   * Mémorise un label pour la reconstruction de contexte route-based.
   */
  rememberObjectLabel(objectType: string, objectId: string, label: string): void {
    if (!objectType || !objectId || !label) return;
    this.labelsByObject.set(this.buildObjectKey(objectType, objectId), label);
  }

  /**
   * Retourne le contexte courant (snapshot).
   */
  getCurrentContext(): NoteContext | null {
    return this.contextSubject.getValue();
  }

  private normalizeUrl(url: string): string {
    if (!url) return "/";

    let normalized = url;
    if (normalized.startsWith("#")) {
      normalized = normalized.slice(1);
    }
    if (!normalized.startsWith("/")) {
      normalized = "/" + normalized;
    }

    const queryIndex = normalized.indexOf("?");
    if (queryIndex >= 0) {
      normalized = normalized.slice(0, queryIndex);
    }

    const hashIndex = normalized.indexOf("#");
    if (hashIndex >= 0) {
      normalized = normalized.slice(0, hashIndex);
    }

    return normalized;
  }

  private buildObjectKey(objectType: string, objectId: string): string {
    return `${objectType}::${objectId}`;
  }
}
